import { Dispatch, SetStateAction, useEffect, useState } from "react";
import styles from "./Placeholder.module.css";
import { Button, Card, Checkbox, Collapsible, Divider, Icon, Label, Select, Text, TextField } from "@shopify/polaris";
import {
    DeleteIcon,
    DiscountIcon,
    DragHandleIcon,
    PlusIcon,
    DuplicateIcon,
    SandboxIcon,
    GiftCardIcon,
    ListBulletedIcon,
    AppsIcon,
} from "@shopify/polaris-icons";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import RenderSelectedProduct2 from "../../../components/RenderSelectedProduct2/RenderSelectedProduct2";
import { BundleTypes, nonEmptyOverride } from "../constants";
import { CollectionsType, PlaceholderStepsType, ProductObj, SubStepType } from "../Types";
import RenderSelectedCollections2 from "../../../components/RenderSelectedCollections2/RenderSelectedCollections2";
// import ImageUploader from "../../../components/ImageUploader/ImageUploader";
import ShopifyNativeError from "../../../components/ShopifyNativeError/ShopifyNativeError";
import { Field } from "@shopify/react-form";
import StepConditions from "../../../components/StepConditions/StepConditions";
import PlaceholderSubStep from "./PlaceholderSubStep";
import { DEFAULT_SUBSTEP } from "./usePlaceholderHook";

const PlaceholderStep = ({
    step,
    snapshot,
    index,
    handleEditStep = () => {},
    // handleDropZoneDrop = () => {},
    isMobile = false,
    steps,
    setExpandStep,
    expandStep,
    addedNow,
    provided,
    selected_template_placeholder,
}: {
    step: PlaceholderStepsType;
    snapshot: any;
    index: number;
    handleEditStep: (
        idx: number,
        keyOrChanges: keyof PlaceholderStepsType | "delete" | "copy" | Record<string, any>,
        value?: any,
    ) => void;
    // handleDropZoneDrop: any;
    isMobile: boolean;
    steps?: Field<PlaceholderStepsType[]>;
    setExpandStep: Dispatch<SetStateAction<number>>;
    expandStep: number;
    addedNow: boolean;
    provided: any;
    selected_template_placeholder: string;
}) => {
    const [hasInteracted, setHasInteracted] = useState(false);
    const [hasInteractedProduct, setHasInteractedProduct] = useState(false);
    const [expandedSubStep, setExpandedSubStep] = useState<number>(-1);
    const [stepErrors, setStepErrors] = useState<{
        step_label: string;
        products: string;
        collections: string;
        product_count_limit: string;
    }>({
        step_label: "",
        products: "",
        collections: "",
        product_count_limit: "",
    });

    useEffect(() => {
        if (hasInteracted) {
            setStepErrors((ps) => ({
                ...ps,
                step_label: nonEmptyOverride(step?.step_label, "Step Label is required"),
            }));
        } else {
            setStepErrors((ps) => ({
                ...ps,
                step_label: "",

                product_count_limit: "",
            }));
        }
    }, [hasInteracted]);

    useEffect(() => {
        if (hasInteractedProduct) {
            setStepErrors((ps) => ({
                ...ps,
                products:
                    step?.products?.length == 0
                        ? "At least one product must be selected for 'Specific Products' selection"
                        : "",
                collections:
                    step?.collections?.length == 0
                        ? `At least one collection must be selected for 'Collection' selection`
                        : "",
            }));
        } else {
            setStepErrors((ps) => ({
                ...ps,
                products: "",
                collections: "",
            }));
        }
    }, [hasInteractedProduct]);

    useEffect(() => {
        if (steps?.error?.includes(`Step ${index + 1}`)) {
            if (steps?.error?.includes("Specific") || steps?.error?.includes("Collection")) {
                setHasInteractedProduct(true);
            } else {
                setHasInteractedProduct(false);
            }
            setHasInteracted(true);
        } else {
            setHasInteracted(false);
            setHasInteractedProduct(false);
        }
    }, [steps?.error]);

    const handleAddCategory = () => {
        const newSubStep: SubStepType = {
            ...DEFAULT_SUBSTEP,
            step_label: `Category ${(step.subSteps?.length || 0) + 1}`,
        };
        const updatedSubSteps = [...(step.subSteps || []), newSubStep];
        handleEditStep(index, "subSteps", updatedSubSteps);
        setExpandedSubStep(updatedSubSteps.length - 1);
    };

    const handleEditSubStep = <K extends keyof SubStepType>(
        stepIdx: number,
        subStepIdx: number,
        key: K | "delete",
        value?: SubStepType[K],
    ) => {
        const currentSubSteps = [...(step.subSteps || [])];

        if (key === "delete") {
            currentSubSteps.splice(subStepIdx, 1);
            handleEditStep(stepIdx, "subSteps", currentSubSteps);
            setExpandedSubStep(-1);
        } else {
            currentSubSteps[subStepIdx] = {
                ...currentSubSteps[subStepIdx],
                [key]: value,
            };
            handleEditStep(stepIdx, "subSteps", currentSubSteps);
        }
    };

    const handleContentModeChange = (enableSubSteps: boolean) => {
        if (enableSubSteps && (!step.subSteps || step.subSteps.length === 0)) {
            // Initialize with empty substeps array in one go
            handleEditStep(index, {
                enable_subSteps: enableSubSteps,
                subSteps: [],
            });
        } else {
            handleEditStep(index, "enable_subSteps", enableSubSteps);
        }
    };

    return (
        <Card
            padding={"300"}
            background={snapshot.isDragging || (expandStep == index && addedNow) ? "bg-surface-active" : "bg-surface"}
        >
            <div
                onClick={() => setExpandStep((ps) => (ps == index ? -1 : index))}
                className={styles.customCardLabelWrapper}
                style={{ marginBottom: "5px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                    <Icon source={DiscountIcon} />
                    <Label id="review_step">{`Step ${index + 1}`}</Label>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandStep == index ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} cursor={"pointer"} />
                    </div>
                </div>
                <div className={styles.stepDragIconWrapper}>
                    <span
                        className={styles.stepDragIcon}
                        style={{ color: "#9b9b9b" }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditStep(index, "copy");
                        }}
                    >
                        <Icon source={DuplicateIcon} />
                    </span>
                    <span {...provided.dragHandleProps} className={styles.stepDragIcon}>
                        <Icon source={DragHandleIcon} tone={snapshot.isDragging ? "textPrimary" : "base"} />
                    </span>
                    <span
                        className={styles.stepDragIcon}
                        style={{ color: "#9b9b9b" }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (index > 0) handleEditStep(index, "delete");
                        }}
                    >
                        <Icon source={DeleteIcon} tone={index == 0 ? "inherit" : "critical"} />
                    </span>
                </div>
            </div>
            <Collapsible
                open={expandStep == index}
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
                id="basic-collapsible"
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        marginTop: 16,
                    }}
                >
                    <div className={styles.bbStepLabels}>
                        <Text as="h3" fontWeight="medium">
                            1. Step Behaviour
                        </Text>
                        <Text as="h6" tone="subdued">
                            How do you want this step to behave?
                        </Text>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(2, 1fr)",
                            gap: 10,
                        }}
                    >
                        <div
                            className={`${styles.stepBehaviourBox} ${
                                step.step_content_type === "products" ? styles.selectedBox : ""
                            }`}
                            onClick={() => handleEditStep(index, "step_content_type", "products")}
                        >
                            {step.step_content_type === "products" && <span className={styles.activeBox}></span>}
                            <span
                                className={step.step_content_type === "products" ? styles.selectedIcon : styles.boxIcon}
                            >
                                <Icon source={SandboxIcon} tone="base" />
                            </span>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Standard Step
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Customers pick items to build their bundle.
                                </Text>
                            </div>
                        </div>
                        <div
                            className={`${styles.stepBehaviourBox} ${
                                step.step_content_type === "free_gift" ? styles.selectedBox : ""
                            }`}
                            onClick={() => handleEditStep(index, "step_content_type", "free_gift")}
                        >
                            {step.step_content_type === "free_gift" && <span className={styles.activeBox}></span>}
                            <span
                                className={
                                    step.step_content_type === "free_gift" ? styles.selectedIcon : styles.boxIcon
                                }
                            >
                                <Icon source={GiftCardIcon} tone="base" />
                            </span>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Free Gift Step
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Customers select a free reward.
                                </Text>
                            </div>
                        </div>
                        {/* <div
                            className={`${styles.stepBehaviourBox} ${
                                step.step_content_type === "fields" ? styles.selectedBox : ""
                            }`}
                            onClick={() => handleEditStep(index, "step_content_type", "fields")}
                        >
                            {step.step_content_type === "fields" && <span className={styles.activeBox}></span>}
                            <span
                                className={step.step_content_type === "fields" ? styles.selectedIcon : styles.boxIcon}
                            >
                                <Icon source={FileIcon} tone="base" />
                            </span>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Custom field step
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Collect notes or text.
                                </Text>
                            </div>
                        </div> */}
                    </div>

                    <div className={styles.bbStepLabels}>
                        <Text as="h3" fontWeight="medium">
                            2. {step.step_content_type === "fields" ? "Fields" : "Product"} Configuration
                        </Text>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                            backgroundColor: "#f5f5f5",
                            padding: 16,
                            borderRadius: 5,
                            border: "1px solid #c1c1c1",
                        }}
                    >
                        {/* Step Info Section */}
                        <div className={styles.bbStepLabels}>
                            <Text as="h3" fontWeight="medium">
                                Step Info
                            </Text>
                        </div>
                        <div
                            className={`${styles.formRow} ${styles.row4}`}
                            style={{
                                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                gap: isMobile ? "8px" : "16px",
                            }}
                        >
                            <TextField
                                label={"Step Label"}
                                placeholder="Step Label"
                                autoComplete={"off"}
                                value={step.step_label}
                                onBlur={() => {
                                    if (!hasInteracted) setHasInteracted(true);
                                }}
                                onChange={(val) => {
                                    setStepErrors((ps) => ({
                                        ...ps,
                                        step_label: nonEmptyOverride(val, "Step Label is required"),
                                    }));
                                    if (!hasInteracted) setHasInteracted(true);
                                    handleEditStep(index, "step_label", val);
                                }}
                                maxLength={120}
                                showCharacterCount
                                error={stepErrors?.step_label}
                            />
                            {selected_template_placeholder !== "accordian" && (
                                <TextField
                                    label={"Step Title (optional)"}
                                    placeholder={"Step Title"}
                                    autoComplete={"off"}
                                    value={step.page_title}
                                    onChange={(val) => {
                                        handleEditStep(index, "page_title", val);
                                    }}
                                    maxLength={80}
                                    showCharacterCount
                                />
                            )}
                        </div>
                        <div className={`${styles.formRow} ${styles.row4}`} style={{ gridTemplateColumns: "1fr" }}>
                            <TextField
                                label={"Step Subtitle (optional)"}
                                placeholder={"Step Subtitle"}
                                autoComplete={"off"}
                                value={step.page_subtitle}
                                onChange={(val) => {
                                    handleEditStep(index, "page_subtitle", val);
                                }}
                                maxLength={200}
                                showCharacterCount
                            />
                        </div>

                        {/* <div className={`${styles.formRow} ${styles.row4}`} style={{ gridTemplateColumns: "1fr" }}>
                            <ImageUploader
                                helpMessage="Recommended size: 100x100px"
                                heading="Step Icon"
                                img_url={step.step_icon?.originalSrc || ""}
                                handleInputChange={(idx: any, key: any, value: any) =>
                                    handleEditStep(idx, key, { originalSrc: value, altText: "Step Icon" })
                                }
                                selectedTab={index}
                                keyName="step_icon"
                            />
                        </div> */}

                        <Divider />

                        {/* Step Content Section */}
                        {(step.step_content_type === "products" || step.step_content_type === "free_gift") && (
                            <>
                                <div className={styles.bbStepLabels}>
                                    <Text as="h3" fontWeight="medium">
                                        Organization
                                    </Text>
                                    <Text as="h6" tone="subdued">
                                        Choose how products are organized in this step.
                                    </Text>
                                </div>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                                        gap: 10,
                                    }}
                                >
                                    <div
                                        className={`${styles.productSelectionTypeBox} ${
                                            step.enable_subSteps ? "" : styles.selectedBox
                                        }`}
                                        onClick={() => handleContentModeChange(false)}
                                    >
                                        {!step.enable_subSteps && <span className={styles.activeBox}></span>}
                                        <span className={step.enable_subSteps ? styles.boxIcon : styles.selectedIcon}>
                                            <Icon source={ListBulletedIcon} tone="base" />
                                        </span>
                                        <div className={styles.bbStepLabels}>
                                            <Text as="h3" fontWeight="medium">
                                                Single List
                                            </Text>
                                        </div>
                                    </div>
                                    <div
                                        className={`${styles.productSelectionTypeBox} ${
                                            step.enable_subSteps ? styles.selectedBox : ""
                                        }`}
                                        onClick={() => handleContentModeChange(true)}
                                    >
                                        {step.enable_subSteps && <span className={styles.activeBox}></span>}
                                        <span className={step.enable_subSteps ? styles.selectedIcon : styles.boxIcon}>
                                            <Icon source={AppsIcon} tone="base" />
                                        </span>
                                        <div className={styles.bbStepLabels}>
                                            <Text as="h3" fontWeight="medium">
                                                Categories
                                            </Text>
                                        </div>
                                    </div>
                                </div>

                                {/* Single Product List Mode */}
                                {!step.enable_subSteps && (
                                    <div className={`${styles.formRow} ${styles.row2}`}>
                                        <div>
                                            <Select
                                                label={"Choose Products"}
                                                options={[
                                                    {
                                                        label: "Specific Products",
                                                        value: "specific",
                                                    },
                                                    {
                                                        label: "Collection",
                                                        value: "collection",
                                                    },
                                                ]}
                                                onChange={(val) => {
                                                    handleEditStep(index, "product_selection", val);
                                                }}
                                                value={step?.product_selection}
                                            />

                                            {step.product_selection == "specific" && (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        marginTop: 10,
                                                        marginBottom: 10,
                                                        paddingBottom: 20,
                                                    }}
                                                >
                                                    <RenderSelectedProduct2
                                                        bundleType={BundleTypes.BB_PLACEHOLDER}
                                                        products={step.products}
                                                        submit={() => {
                                                            steps?.runValidation();
                                                            if (!hasInteractedProduct) setHasInteractedProduct(true);
                                                        }}
                                                        isMobile={isMobile}
                                                        multiple={true}
                                                        handleFieldChange={(value: ProductObj[]) => {
                                                            setStepErrors((ps) => ({
                                                                ...ps,
                                                                products:
                                                                    value?.length == 0
                                                                        ? "At least one product must be selected for 'Specific Products' selection"
                                                                        : "",
                                                            }));
                                                            if (!hasInteractedProduct) setHasInteractedProduct(true);
                                                            handleEditStep(index, "products", value);
                                                        }}
                                                    />
                                                    {!!stepErrors?.products?.length && hasInteractedProduct && (
                                                        <ShopifyNativeError
                                                            error={stepErrors.products}
                                                            style={{ marginTop: 5, paddingLeft: 2 }}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {step.product_selection == "collection" && (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        marginTop: 10,
                                                        marginBottom: 10,
                                                        width: "100%",
                                                    }}
                                                >
                                                    <RenderSelectedCollections2
                                                        collections={step.collections}
                                                        multiple={1}
                                                        submit={() => {
                                                            steps?.runValidation();
                                                            if (!hasInteractedProduct) setHasInteractedProduct(true);
                                                        }}
                                                        handleFieldChange={(value: CollectionsType[]) => {
                                                            setStepErrors((ps) => ({
                                                                ...ps,
                                                                collections:
                                                                    value?.length == 0
                                                                        ? "At least one product must be selected for 'Collection' selection"
                                                                        : "",
                                                            }));
                                                            if (!hasInteractedProduct) setHasInteractedProduct(true);

                                                            handleEditStep(index, "collections", value);
                                                        }}
                                                    />
                                                    {!!stepErrors?.collections?.length && hasInteractedProduct && (
                                                        <ShopifyNativeError
                                                            error={stepErrors.collections}
                                                            style={{ marginTop: 5, paddingLeft: 2 }}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Categories Mode */}
                                {step.enable_subSteps && (
                                    <div style={{ marginTop: "10px" }}>
                                        {!step.subSteps || step.subSteps.length === 0 ? (
                                            <div
                                                style={{
                                                    backgroundColor: "#f9fafb",
                                                    borderRadius: "8px",
                                                    padding: "32px 24px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#374151" }}>
                                                    No categories added yet
                                                </p>
                                                <p style={{ margin: "0 0 16px 0", color: "#6b7280", fontSize: "14px" }}>
                                                    Create categories to group products like Base, Add-ons, or Extras.
                                                </p>
                                                <Button onClick={handleAddCategory} icon={PlusIcon}>
                                                    Add Category
                                                </Button>
                                            </div>
                                        ) : (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                                {step.subSteps.map((subStep, subStepIdx) => (
                                                    <PlaceholderSubStep
                                                        key={subStepIdx}
                                                        subStep={subStep}
                                                        subStepIndex={subStepIdx}
                                                        stepIndex={index}
                                                        handleEditSubStep={handleEditSubStep}
                                                        isMobile={isMobile}
                                                        expandedSubStep={expandedSubStep}
                                                        setExpandedSubStep={setExpandedSubStep}
                                                    />
                                                ))}
                                                <Button onClick={handleAddCategory} icon={PlusIcon} variant="plain">
                                                    Add Category
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        <Divider />

                        {/* Step Rules Section */}
                        <div className={styles.bbStepLabels}>
                            <Text as="h3" fontWeight="medium">
                                Step Rules
                            </Text>
                        </div>

                        <div className={`${styles.formRow} ${styles.row6}`}>
                            <Checkbox
                                label={"Show Variants as Individual Products"}
                                checked={step.render_as_variants}
                                onChange={(val) => {
                                    handleEditStep(index, "render_as_variants", val);
                                }}
                            />
                        </div>

                        <div className={`${styles.formRow} ${styles.row6}`}>
                            <Checkbox
                                label={"Add Conditions for This Step"}
                                checked={step.show_step_conditions}
                                onChange={(val) => {
                                    handleEditStep(index, "show_step_conditions", val);
                                }}
                            />
                        </div>

                        <Divider />
                        {step?.show_step_conditions &&
                            (step.step_content_type == "products" || step.step_content_type == "free_gift") && (
                                <StepConditions
                                    step={step}
                                    handleEditStep={handleEditStep as (idx: number, key: string, value?: any) => void}
                                    stepIdx={index}
                                    styles={styles}
                                    bundle_type={BundleTypes.BB_PLACEHOLDER}
                                />
                            )}
                        {/* Steps Conditions Ends */}
                    </div>
                </div>
            </Collapsible>
        </Card>
    );
};

export default PlaceholderStep;
